import React, { useState } from 'react';
import { twoFactorAPI } from '../services/api';
import { toast } from 'react-hot-toast';
import { Shield, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useNavigate } from 'react-router-dom';

interface TwoFactorVerifyProps {
  userId: number;
  onBack: () => void;
}

const TwoFactorVerify: React.FC<TwoFactorVerifyProps> = ({ userId, onBack }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();

    if (code.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await twoFactorAPI.verifyLogin(userId, code);
      
      // Store auth token and user info
      setAuth(response.data.token, response.data.user);
      
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Invalid verification code');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ 
      background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)'
    }}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="flex items-center justify-center mb-6">
          <div className="p-4 rounded-full bg-blue-100 dark:bg-blue-900/30">
            <Shield className="h-8 w-8" style={{ color: 'var(--color-primary)' }} />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-2">
          Two-Factor Authentication
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-8">
          Enter the 6-digit code from your authenticator app
        </p>

        <form onSubmit={handleVerify} className="space-y-6">
          <div>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full px-4 py-4 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-center font-mono text-2xl tracking-widest focus:ring-2 focus:ring-offset-0 focus:outline-none text-gray-900 dark:text-white"
              style={{ '--tw-ring-color': 'var(--color-primary)' } as any}
              maxLength={6}
              autoFocus
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full py-3 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            {loading ? 'Verifying...' : 'Verify & Continue'}
          </button>

          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="w-full py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Login</span>
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Can't access your authenticator app? Contact support for assistance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorVerify;











